import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye,
  Download,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { sipotmGetWorkOrders, sipotmDeleteWorkOrder, sipotmGetWorkOrderFileUrl } from "../api";
import "./SIPOTMWorkOrdersList.css";

const SIPOTMWorkOrdersList = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [formTypeFilter, setFormTypeFilter] = useState("");
  
  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchWorkOrders();
  }, [currentPage, statusFilter, formTypeFilter]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: currentPage * pageSize,
        limit: pageSize,
      };
      
      if (statusFilter) params.status = statusFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      
      const data = await sipotmGetWorkOrders(params);
      setWorkOrders(data?.work_orders || []);
      setTotalRecords(data?.total || 0);
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
      setError(err.message || "Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchWorkOrders();
  };

  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      await sipotmDeleteWorkOrder(id);
      setDeleteId(null);
      fetchWorkOrders();
    } catch (err) {
      console.error("Failed to delete work order:", err);
      alert("Failed to delete work order: " + (err.message || "Unknown error"));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = (workOrderId) => {
    const url = sipotmGetWorkOrderFileUrl(workOrderId);
    window.open(url, "_blank");
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed": return "status-completed";
      case "failed": return "status-failed";
      case "processing":
      case "extracting":
      case "detecting_form_type": return "status-processing";
      default: return "status-pending";
    }
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <div className="sipotm-workorders">
      {/* Header */}
      <div className="sipotm-workorders-header">
        <h1>Work Orders</h1>
        <button className="sipotm-refresh-btn" onClick={fetchWorkOrders} disabled={loading}>
          <RefreshCw size={18} className={loading ? "spinning" : ""} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="sipotm-filters-card">
        <form onSubmit={handleSearch} className="sipotm-filters">
          <div className="sipotm-search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by UID, filename, or work order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="sipotm-filter-row">
            <div className="sipotm-filter-group">
              <Filter size={16} />
              <select 
                value={statusFilter} 
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="extracting">Extracting</option>
              </select>
            </div>

            <div className="sipotm-filter-group">
              <select 
                value={formTypeFilter} 
                onChange={(e) => { setFormTypeFilter(e.target.value); setCurrentPage(0); }}
              >
                <option value="">All Form Types</option>
                <option value="sip">SIP</option>
                <option value="otm">OTM</option>
                <option value="sip_otm">SIP + OTM</option>
              </select>
            </div>
          </div>

          <button type="submit" className="sipotm-search-btn">
            Search
          </button>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="sipotm-error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={fetchWorkOrders}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="sipotm-table-card">
        <table className="sipotm-table">
          <thead>
            <tr>
              <th>UID / Filename</th>
              <th>Form Type</th>
              <th>AMC</th>
              <th>Status</th>
              <th>Processing Time</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="sipotm-loading-cell">
                  <div className="sipotm-loading-spinner"></div>
                  Loading work orders...
                </td>
              </tr>
            ) : workOrders.length === 0 ? (
              <tr>
                <td colSpan="7" className="sipotm-empty-cell">
                  <FileText size={48} />
                  <p>No work orders found</p>
                  {(searchQuery || statusFilter || formTypeFilter) && (
                    <button onClick={() => { setSearchQuery(""); setStatusFilter(""); setFormTypeFilter(""); }}>
                      Clear filters
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              workOrders.map((wo) => (
                <tr key={wo._id || wo.work_order_id}>
                  <td>
                    <div className="sipotm-cell-primary">
                      <FileText size={16} />
                      <div>
                        <div className="sipotm-uid">{wo.uid || '-'}</div>
                        <div className="sipotm-filename">{wo.filename || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`sipotm-form-type ${wo.form_type || ''}`}>
                      {wo.form_type?.toUpperCase() || '-'}
                    </span>
                  </td>
                  <td>
                    <span className="sipotm-amc">{wo.amc || '-'}</span>
                  </td>
                  <td>
                    <span className={`sipotm-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status || 'pending'}
                    </span>
                  </td>
                  <td>
                    {wo.processing_time ? `${wo.processing_time.toFixed(2)}s` : '-'}
                  </td>
                  <td>
                    {wo.created_at ? new Date(wo.created_at).toLocaleString() : '-'}
                  </td>
                  <td>
                    <div className="sipotm-actions">
                      <button
                        className="sipotm-action-btn view"
                        onClick={() => navigate(`/sip-otm/work-order/${wo._id || wo.work_order_id}`)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="sipotm-action-btn download"
                        onClick={() => handleDownload(wo._id || wo.work_order_id)}
                        title="Download File"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="sipotm-action-btn delete"
                        onClick={() => setDeleteId(wo._id || wo.work_order_id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && totalRecords > 0 && (
          <div className="sipotm-pagination">
            <div className="sipotm-pagination-info">
              Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalRecords)} of {totalRecords}
            </div>
            <div className="sipotm-pagination-btns">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              <span className="sipotm-page-indicator">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="sipotm-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="sipotm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Work Order</h3>
            <p>Are you sure you want to delete this work order? This action cannot be undone.</p>
            <div className="sipotm-modal-actions">
              <button className="sipotm-modal-cancel" onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button 
                className="sipotm-modal-delete" 
                onClick={() => handleDelete(deleteId)}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SIPOTMWorkOrdersList;
